import { useContext, useEffect, useRef, useState } from 'react'

import { Button } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { FormContext } from 'Components/Hooks/ContextForm'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import InputsWithValidate from 'Components/UI/Molecules/InputsWithValidate'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { QuotationFields } from 'Components/UI/Molecules/Vehicles/SearchVehicles/DialogVehicle/Content/Tabs/Content/Quotation/Modals/Fields'
import { formNew } from 'Components/Validation/Vehicles/SearchVehicles'
import { format, isValid } from 'date-fns'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { interfaces } from 'service/interfaces'

import useStyles from '../../../../../../../StyleModalNew'

const TAB_FLOW_FIELDS = [
  'proveedor',
  'proveedor_manual',
  'fecha_factura',
  'fecha_pago',
  'suc_factura',
  'nro_factura',
  'monto_total',
  'tipo',
  'observaciones',
]

const New = (props) => {
  const { formEdit } = useContext(FormContext)
  const { handleCloseMain } = props
  const { enqueueSnackbar } = useSnackbar()
  const id = formEdit.vehicle.id

  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const formSubmitRef = useRef(null)

  const isNodeFocusable = (element) => {
    if (!element) return false
    const isDisabled = element.disabled || element.getAttribute('aria-disabled') === 'true'
    const isHidden =
      element.getAttribute('aria-hidden') === 'true' ||
      element.offsetParent === null ||
      window.getComputedStyle(element).visibility === 'hidden'
    return !isDisabled && !isHidden && element.tabIndex !== -1
  }

  const getFieldNode = (fieldName, rootElement) => {
    if (!rootElement) return null
    const selectors = [
      `input[name="${fieldName}"]`,
      `textarea[name="${fieldName}"]`,
      `[name="${fieldName}"][tabindex]`,
    ]
    for (const selector of selectors) {
      const candidates = Array.from(rootElement.querySelectorAll(selector))
      const found = candidates.find(isNodeFocusable)
      if (found) return found
    }
    return null
  }

  const getCurrentFieldName = () => {
    const active = document.activeElement
    if (!active) return null
    const directName = active.getAttribute('name')
    if (directName) return directName
    const namedParent = active.closest('[name]')
    return namedParent ? namedParent.getAttribute('name') : null
  }

  const getNextFieldName = (currentFieldName, step, rootElement) => {
    const currentIndex = TAB_FLOW_FIELDS.indexOf(currentFieldName)
    if (currentIndex === -1) return null

    for (
      let nextIndex = currentIndex + step;
      nextIndex >= 0 && nextIndex < TAB_FLOW_FIELDS.length;
      nextIndex += step
    ) {
      const nextFieldName = TAB_FLOW_FIELDS[nextIndex]
      if (getFieldNode(nextFieldName, rootElement)) return nextFieldName
    }

    return null
  }

  // En este diálogo, algunos inputs de MUI (Autocomplete/DatePicker) pueden re-renderizarse
  // después de Tab y devolver el foco al primer campo. Reintentar el foco en frames siguientes
  // mantiene estable la navegación por teclado.
  const focusFieldWithRetry = (fieldName, rootElement, maxAttempts = 4) => {
    const tryFocus = (attempt) => {
      const targetNode = getFieldNode(fieldName, rootElement)
      if (!targetNode) return

      targetNode.focus()
      const focusedFieldName = getCurrentFieldName()

      if (focusedFieldName === fieldName) return
      if (attempt >= maxAttempts) return

      window.requestAnimationFrame(() => tryFocus(attempt + 1))
    }

    window.requestAnimationFrame(() => tryFocus(0))
  }

  const handleSubmit = () => {
    formSubmitRef.current.click()
  }

  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  const formatDateForBackend = (value) => {
    if (!value) return null
    if (!(value instanceof Date) || !isValid(value)) return null
    return format(value, 'yyyy-MM-dd')
  }

  const onSubmit = (values) => {
    const payload = {
      ...values,
      proveedor: values.proveedor || values.proveedor_manual,
      fecha_factura: formatDateForBackend(values.fecha_factura),
      fecha_pago: formatDateForBackend(values.fecha_pago),
    }

    delete payload.proveedor_manual

    interfaces.vehicleInterface
      .newPayment(id, payload)
      .then((res) => {
        enqueueSnackbar(res.data, { variant: 'success' })
        handleCloseMain()
      })
      .catch(() => {
        enqueueSnackbar('Algo salió mal!', { variant: 'error' })
      })
  }

  const [vehicles, setVehicles] = useState([])
  const [companies, setCompanies] = useState([])
  const [types, setTypes] = useState([])

  async function getVehiclesForPayment() {
    await interfaces.vehicleInterface.getAllVehicleForPayments().then((res) => {
      setVehicles(res.data)
    })
  }

  async function getData() {
    await interfaces.vehicleInterface.getCompanies().then((res) => {
      setCompanies(res.data)
    })
    await interfaces.conceptsInterface.getConcepts().then((res) => {
      setTypes(res.data)
    })
  }

  useEffect(() => {
    getData()
  }, [])

  const handleFormKeyDownCapture = (event) => {
    if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return

    const rootElement = event.currentTarget
    const step = event.shiftKey ? -1 : 1
    const nextFieldName = getNextFieldName(getCurrentFieldName(), step, rootElement)
    if (!nextFieldName) return

    event.preventDefault()
    event.stopPropagation()
    focusFieldWithRetry(nextFieldName, rootElement)
  }

  const children = () => {
    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          proveedor: '',
          proveedor_manual: '',
          fecha_factura: null,
          fecha_pago: null,
          suc_factura: '',
          nro_factura: '',
          monto_total: '',
          observaciones: '',
          tipo: '',
          //vehiculos: [],
        }}
        validationSchema={formNew}
      >
        {({ setFieldValue, isSubmitting, values }) => (
          <Form onKeyDownCapture={handleFormKeyDownCapture}>
            <InputsWithValidate
              fields={QuotationFields(
                companies,
                types,
                vehicles,
                setFieldValue,
                values,
                getVehiclesForPayment
              )}
              spacing={2}
              setFieldValue={setFieldValue}
              isSubmitting={isSubmitting}
              strictManualDate={true}
            />

            <button style={{ display: 'none' }} type="submit" ref={formSubmitRef} />
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <div>
      <Button
        className={classes.textTransformLetter}
        variant="contained"
        color="primary"
        type="submit"
        startIcon={<AddIcon />}
        onClick={handleOpen}
      >
        Nuevo
      </Button>
      <DialogComponent
        customHeight="auto"
        widthLarge="30vw"
        tipo="center"
        open={open}
        disableBackdropClick={true}
        handleclose={handleClose}
        title="Nuevo costo/gasto"
        children={children()}
        actions={
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginRight: '2px',
            }}
          >
            <CancelConfirmButtons
              onClickCancel={handleClose}
              onClickSubmit={handleSubmit}
              submitText="Guardar"
            />
          </div>
        }
      />
    </div>
  )
}

export default New
